import { inject } from '@angular/core';

import { AuthService } from '@blaze-shared/auth-data-access';
import {
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigSubsection,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { DEFAULT_ICON_TO_TEXT_RATIO } from '@blaze-shared/theming-domain';

import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { type UploadFilePropsModel } from './upload-file.model';

export const UPLOAD_FILE_DEFAULT_WIDTH = 168;
export const UPLOAD_FILE_MIN_SIZE = 48;
export const UPLOAD_FILE_MAX_WIDTH = 900;
export const UPLOAD_FILE_MAX_HEIGHT = 150;
export const UPLOAD_FILE_WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
export const UPLOAD_FILE_HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];

const UPLOAD_BUTTON_STYLE_CONFIG: PropertiesConfigSubsection[] = [
  epc.subsections.subsection(
    'style.upload',
    'Button Label',
    [
      epc.fields.colorField('style.upload.label', 'Label', {
        allowGradient: false,
        allowNone: false,
      }),
      epc.fields.horizontalAlignField('style.upload.align'),
      ...epc.fields.fontFamilyAndWeightFields(
        'style.upload.font.fontFamily',
        'style.upload.font.fontWeight',
      ),
      epc.fields.fontStyleField('style.upload.font.style', 'Style', ['bold', 'line-through']),
      epc.utils.observedField(
        epc.fields.sliderField('style.upload.font.fontSize', 'Font size', [
          { min: 8, max: 72, unit: 'px' },
        ]),
        (model: UploadFilePropsModel, value: ValueWithUnitOfMeasure) => {
          const fontSizeNumber = Number.parseInt(value);
          const lineHeightValidation = validateLineHeightWithFontSize(
            model,
            value,
            'style.upload.font.lineHeight',
          );

          if (Number.isNaN(fontSizeNumber)) {
            // If number is unknown, return default icon size
            return {
              ...lineHeightValidation,
              'style.upload.iconSize': '$t.buttonIconSize',
            };
          }

          // Calculate icon size based on font size
          const iconSizeString = `${Math.round(fontSizeNumber * DEFAULT_ICON_TO_TEXT_RATIO)}px`;
          return {
            ...lineHeightValidation,
            'style.upload.iconSize': iconSizeString,
          };
        },
      ),
      epc.utils.observedField(
        epc.fields.sliderField('style.upload.font.lineHeight', 'Line height', [
          { min: 8, max: 72, unit: 'px' },
        ]),
        (props, value: ValueWithUnitOfMeasure) => {
          return validateFontSizeWithLineHeight(props, value, 'style.upload.font.fontSize');
        },
      ),

      epc.fields.sliderField('style.upload.iconSize', 'Icon size', [
        { min: 8, max: 48, unit: 'px' },
      ]),
    ],
    {
      hideWhen: (_, __, props: UploadFilePropsModel) => props.content.options.style === 'drag-drop',
    },
  ),

  epc.subsections.subsection(
    'style.button',
    'Button',
    [
      epc.fields.colorField('style.upload.fill', 'Fill', {}),
      epc.fields.colorField('style.upload.outline', 'Outline', {
        allowGradient: false,
        allowNone: true,
      }),
      epc.fields.sliderField(`style.button.outlineWidth`, 'Outline width', [
        {
          min: 0,
          max: 10,
          unit: 'px',
        },
      ]),
      epc.fields.radiusField(`style.button.radius`),
    ],
    {
      hideWhen: (_, __, props: UploadFilePropsModel) => props.content.options.style === 'drag-drop',
    },
  ),
];

const DRAG_DROP_STYLE_CONFIG: PropertiesConfigSubsection[] = [
  epc.subsections.subsection(
    'style.upload',
    'Drag and Drop',
    [
      epc.fields.colorField('style.dragDrop.label', 'Label', {
        allowGradient: false,
        allowNone: false,
      }),
      epc.fields.colorField('style.dragDrop.action', 'Action', {
        allowGradient: false,
        allowNone: false,
      }),
      epc.fields.horizontalAlignField('style.dragDrop.align'),
      ...epc.fields.fontFamilyAndWeightFields(
        'style.dragDrop.font.fontFamily',
        'style.dragDrop.font.fontWeight',
      ),
      epc.fields.fontStyleField('style.dragDrop.font.style', 'Style', ['bold', 'line-through']),
      epc.utils.observedField(
        epc.fields.sliderField('style.dragDrop.font.fontSize', 'Font size', [
          { min: 8, max: 72, unit: 'px' },
        ]),
        (model: UploadFilePropsModel, value: ValueWithUnitOfMeasure) => {
          const lineHeightValidation = validateLineHeightWithFontSize(
            model,
            value,
            'style.dragDrop.font.lineHeight',
          );

          // Calculate icon size based on font size
          return {
            ...lineHeightValidation,
          };
        },
      ),
      epc.utils.observedField(
        epc.fields.sliderField('style.dragDrop.font.lineHeight', 'Line height', [
          { min: 8, max: 72, unit: 'px' },
        ]),
        (props, value: ValueWithUnitOfMeasure) => {
          return validateFontSizeWithLineHeight(props, value, 'style.dragDrop.font.fontSize');
        },
      ),

      epc.fields.colorField('style.dragDrop.fill', 'Fill', {}),
      epc.fields.colorField('style.dragDrop.outline', 'Outline', {
        allowGradient: false,
        allowNone: true,
      }),
      epc.fields.sliderField(`style.dragDrop.outlineWidth`, 'Outline width', [
        {
          min: 0,
          max: 10,
          unit: 'px',
        },
      ]),
      epc.fields.radiusField(`style.dragDrop.radius`),
    ],
    {
      hideWhen: (_, __, props: UploadFilePropsModel) => !props.content.options.style,
    },
  ),
];

export const UPLOAD_ELEMENT_STYLE_CONFIG: PropertiesConfigSubsection[] = [
  epc.subsections.styleSheetSubsection(),
  epc.subsections.subsection('style.general', 'Margin', [
    epc.fields.spacingAndAlignField('style.general.offset', {
      hasMargin: true,
    }),
  ]),
  epc.subsections.subsection('style.general', 'General', [
    epc.fields.colorField(`style.general.title`, 'Title', {
      allowGradient: false,
      allowNone: false,
    }),
  ]),

  ...UPLOAD_BUTTON_STYLE_CONFIG,
  ...DRAG_DROP_STYLE_CONFIG,

  epc.subsections.subsection('style.uploaded', 'Uploaded Field', [
    epc.fields.colorField('style.uploaded.label', 'Label', {
      allowGradient: false,
      allowNone: false,
    }),
    epc.fields.colorField('style.uploaded.fill', 'Fill', {}),
    epc.fields.colorField('style.uploaded.outline', 'Outline', {
      allowGradient: false,
      allowNone: true,
    }),
  ]),

  epc.subsections.labelStyleSubsection(
    {
      color: 'style.general.title',
      enabled: 'content.title.enabled',
      fontFamily: 'style.labelBlock.fontFamily',
      fontSize: 'style.labelBlock.fontSize',
      fontStyle: 'style.labelBlock.fontStyle',
      fontWeight: 'style.labelBlock.fontWeight',
      horizontalAlign: 'content.title.align',
      icon: 'content.title.icon',
      iconColor: 'content.title.iconColor',
      iconPosition: 'content.title.iconPosition',
      iconSize: 'style.labelBlock.iconSize',
      lineHeight: 'style.labelBlock.lineHeight',
      position: 'content.title.position',
      width: 'content.title.width',
    },
    {
      excludedPositions: ['right'],
    },
  ),
];

export function getUploadFilePropsConfig(): PropertiesConfig {
  return epc.initConfig('uploadFile', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.button',
        'Button',
        [
          epc.fields.textInput('content.button.label', 'Label'),
          epc.fields.iconField('content.button.icon'),
          epc.fields.iconPositionField('content.button.iconPosition', 'content.button.icon'),
          epc.fields.toggleField('content.button.readonly', 'Read Only'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'File URL',
              sources: 'field',
              bindings: [{ property: 'value', label: 'File URL', dataType: 'String' }],
            },
          ],
        },
      ),
      epc.subsections.subsection('content.options', 'Options', [
        epc.fields.radiosField('content.options.style', 'Style', [
          { label: 'Button', value: undefined },
          { label: 'Drag & Drop', value: 'drag-drop' },
        ]),
        epc.fields.toggleField('content.options.multiple', 'Allow multiple files', {
          renderType: 'toggle',
        }),
        epc.fields.toggleField('content.fileFormats.enabled', 'Allow All File Formats', {
          renderType: 'toggle',
          disableWhen: (props) =>
            props?.content?.options?.capture?.enabled
              ? 'Disabled when capture option is enabled'
              : undefined,
        }),
        ...epc.utils.conditionalFields(
          epc.utils.conditionalFields(
            [
              epc.fields.toggleField('content.fileFormats.csv', 'CSV files', {
                renderType: 'check',
              }),
              epc.fields.toggleField('content.fileFormats.docs', 'Document files', {
                renderType: 'check',
              }),
              epc.fields.toggleField('content.fileFormats.image', 'Image files', {
                renderType: 'check',
              }),
              epc.fields.toggleField('content.fileFormats.video', 'Video files', {
                renderType: 'check',
              }),
              epc.fields.toggleField('content.fileFormats.audio', 'Audio files', {
                renderType: 'check',
              }),
            ],
            'content.fileFormats.enabled',
            (value) => !!value,
          ),
          'content.options.capture.enabled',
          (value) => !!value,
        ),
        epc.utils.observedField(
          epc.fields.toggleField('content.options.capture.enabled', 'Allow only camera capture', {
            renderType: 'toggle',
            tooltip: 'Mobile only',
          }),
          (_, value) => {
            if (value) {
              return { 'content.fileFormats.enabled': false };
            }
            return {};
          },
        ),
        ...epc.utils.conditionalFields(
          [
            epc.fields.radiosField(
              'content.options.capture.format',
              'Format allowed',
              [
                { label: 'Image', value: 'image' },
                { label: 'Video', value: 'video' },
              ],
              { renderType: 'icon-text' },
            ),
            epc.utils.conditionalField(
              epc.fields.toggleField('content.options.capture.saveAsMp4', 'Save as .mp4', {
                renderType: 'toggle',
                tooltip:
                  'Changes the file extension to .mp4 without converting it. Helps with services that handle the video but reject its original extension (e.g. .mov).',
              }),
              'content.options.capture.format',
              // Show only for video capture, and only to Root/Dev users
              (value) => value !== 'video' || !inject(AuthService).isCurrentUserAccountRootOrDev(),
            ),
            epc.fields.radiosField(
              'content.options.capture.defaultCamera',
              'Default camera',
              [
                { label: 'Front', value: 'user' },
                { label: 'Back', value: 'environment' },
              ],
              { renderType: 'icon-text' },
            ),
          ],
          'content.options.capture.enabled',
          (value) => !value,
        ),

        epc.utils.conditionalField(
          epc.utils.separatedField(
            epc.fields.radiosField('content.options.saveAs', 'Save As', [
              { label: 'URL', value: 'url' },
              { label: 'Base64', value: 'base64' },
            ]),
            { hideSeparatorWhenHidden: true },
          ),
          'data.?key:value.enabled',
          (value) => !!value,
        ),
      ]),

      epc.subsections.subsection('content', 'Action', [epc.fields.buttonActionField('')], {
        hideLabel: true,
      }),

      epc.subsections.elementSizeSubsection(
        UPLOAD_FILE_MIN_SIZE,
        UPLOAD_FILE_MAX_WIDTH,
        UPLOAD_FILE_MAX_HEIGHT,
        UPLOAD_FILE_WIDTH_OPTIONS,
        UPLOAD_FILE_HEIGHT_OPTIONS,
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section('style', 'Style', UPLOAD_ELEMENT_STYLE_CONFIG, true),
  ]);
}
